import {
  addDefaultProps,
  addDisplayName,
  addHandlers,
  addProps,
  addRef,
  addState,
  flowMax,
} from 'ad-hok'
import { addLayoutEffectOnMount } from 'ad-hok-utils'
import cx from 'classnames'
import { JssStyle } from 'jss'
import React, {
  ChangeEvent,
  ComponentProps,
  FC,
  InputHTMLAttributes,
} from 'react'
import { createUseStyles } from 'react-jss'
import { typedAs } from 'shared/src/utils/typedAs'

import { INPUT_NAME_Z_INDEX } from '../utils/zIndex'
import { Button } from './Button'
import { Field, FieldNew } from './Field'
import { IconButton } from './IconButton'
import { styles as textStyles } from './Text'
import { BODY_MEDIUM_STYLE } from './styles'

const styles = {
  label: cx(
    'absolute',
    'text-lg',
    'text-rodeo-grey/96',
    'duration-100',
    'transform',
    '-translate-y-4',
    'scale-[.65]',
    'top-4',
    'z-10', // TODO: replace this with INPUT_NAME_Z_INDEX when de-Tailwind-ify'ing
    'origin-[0]',
    'left-2.5',
    'peer-focus:scale-[.65]',
    'peer-focus:-translate-y-4',
    'peer-focus:leading-label',
    'peer-focus:tracking-wide',
    'peer-placeholder-shown:scale-100',
    'peer-placeholder-shown:translate-y-0',
    'peer-disabled:-translate-y-4',
    'peer-disabled:leading-label',
    'peer-disabled:tracking-wide',
    'peer-disabled:scale-[.65]',
    'pointer-events-none'
  ),
  labelError: cx('text-rodeo-errors'),
  input: cx(
    'relative',
    'px-2.5',
    'pb-2.5',
    'w-full',
    'text-sm',
    'text-rodeo-grey/96',
    'appearance-none',
    'focus:outline-none',
    'focus:ring-0',
    'focus:border-rodeo-secondary1/56',
    'peer',
    'rounded-sm',
    'border-rodeo-secondary1/24',
    'bg-rodeo-grey/[.02]',
    'focus:bg-rodeo-grey/[.04]',
    'caret-rodeo-secondary1',
    'disabled:bg-rodeo-input-bg/[.08]'
  ),
  inputError: cx(
    'bg-rodeo-errors/[.04]',
    'border-rodeo-errors/24',
    'focus:bg-rodeo-errors/[.04]',
    'focus:border-rodeo-errors/56'
  ),
  area: cx('resize-y'),
  clearButton: cx('absolute', 'right-2.5', 'min-h-full'),
  clearButtonIcon: cx(
    'text-rodeo-secondary1/56',
    'hover:text-rodeo-secondary1/96'
  ),
  clearButtonErrorIcon: cx(
    'text-rodeo-errors/56',
    'hover:text-rodeo-errors1/96'
  ),
  hasLabel: 'pt-4',
}

const INPUT_LEFT_PADDING = 16
const INPUT_HEIGHT = 64

const labelShrunkStyle: JssStyle = {
  transform: 'translateY(-5px) scale(0.75)',
  transformOrigin: 'top left',
}

const useStyles = createUseStyles({
  inputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  input: {
    height: INPUT_HEIGHT,
    paddingLeft: INPUT_LEFT_PADDING,
    paddingRight: 40,
    paddingTop: 29.5,
    ...BODY_MEDIUM_STYLE,
    color: 'rgba(54, 52, 50, 0.8)',
    backgroundColor: 'rgba(54, 52, 50, 0.02)',
    border: '1px solid rgba(144, 115, 79, 0.24)',
    borderRadius: 2,
    caretColor: '#90734F',
    '&:focus': {
      backgroundColor: 'rgba(54, 52, 50, 0.04)',
      borderColor: 'rgba(144, 115, 79, 0.56)',
      outline: 'none',
    },
    '&::placeholder': {
      color: 'rgba(144, 115, 79, 0.24)',
      opacity: 1,
      visibility: 'hidden',
    },
    '&:focus::placeholder': {
      visibility: 'visible',
    },
  },
  textarea: {
    resize: 'vertical',
    height: 'auto',
  },
  inputNoLabel: {
    paddingTop: 10,
    '&::placeholder': {
      visibility: 'visible',
    },
  },
  inputError: {
    // force this to prevail vs inputClassName
    'html &': {
      backgroundColor: 'rgba(245, 117, 97, 0.08)',
      borderColor: 'rgba(245, 117, 97, 0.24)',
      '&:focus': {
        backgroundColor: 'rgba(245, 117, 97, 0.08)',
        borderColor: 'rgba(245, 117, 97, 0.56)',
      },
    },
  },
  label: {
    position: 'absolute',
    left: INPUT_LEFT_PADDING,
    top: 0,
    height: INPUT_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    zIndex: INPUT_NAME_Z_INDEX,
    pointerEvents: 'none',
    ...BODY_MEDIUM_STYLE,
    color: 'rgba(54, 52, 50, 0.8)',
    transitionDuration: '100ms',
    'input:focus + &': labelShrunkStyle,
    'textarea:focus + &': labelShrunkStyle,
  },
  labelShrunk: labelShrunkStyle,
  labelError: {
    color: '#F57561',
  },
  clearButtonContainer: {
    position: 'absolute',
    right: 2,
    top: 0,
    height: INPUT_HEIGHT,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  clearButton: {
    color: 'rgba(144, 115, 79, 0.56)',
    '&:hover': {
      color: 'rgba(144, 115, 79, 0.96)',
    },
  },
  clearButtonError: {
    color: 'rgba(245, 117, 97, 0.56)',
    '&:hover': {
      color: 'rgba(245, 117, 97, 0.96)',
    },
  },
})

interface Props extends ComponentProps<typeof Field> {
  value: string | undefined | null
  onChange: (value: string) => void
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onFocus?: () => void
  inputProps?: InputHTMLAttributes<HTMLInputElement>
  onClear?: () => void
  inputClassName?: string
  wrapperClassName?: string
}

export const TextField: FC<Props> = flowMax(
  addDisplayName('TextField'),
  addHandlers({
    onChange:
      ({ onChange }) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value)
      },
    onClear:
      ({ onChange }) =>
      () =>
        onChange(''),
  }),
  addRef('ref', typedAs<HTMLInputElement | null>(null)),
  addLayoutEffectOnMount(({ focusOnMount, ref }) => () => {
    if (focusOnMount) {
      ref.current?.focus()
    }
  }),
  ({
    id,
    label,
    value,
    onChange,
    onFocus,
    onKeyPress,
    onClear,
    inputProps,
    error,
    disabled,
    noMargin,
    ref,
    suggestion,
    placeholder,
    className,
    inputClassName,
  }) => (
    <Field
      id={id}
      error={error}
      suggestion={suggestion}
      disabled={disabled}
      noMargin={noMargin}
      className={className}
    >
      <input
        name={id}
        id={id}
        onChange={onChange}
        onFocus={onFocus}
        onKeyUp={onKeyPress}
        type="text"
        className={cx(
          styles.input,
          textStyles.body1,
          label && styles.hasLabel,
          inputClassName,
          error && styles.inputError
        )}
        value={value ?? ''}
        disabled={disabled}
        ref={ref}
        placeholder={placeholder ?? ''}
        spellCheck="false"
        autoCorrect="off"
        autoCapitalize="off"
        {...inputProps}
      />
      {label && (
        <label
          htmlFor={id}
          className={cx(styles.label, error && styles.labelError)}
        >
          {label}
        </label>
      )}
      {value && (
        <Button
          type="image16"
          onClick={onClear}
          icon="clear-input"
          className={styles.clearButton}
          iconClassName={
            error ? styles.clearButtonErrorIcon : styles.clearButtonIcon
          }
        />
      )}
    </Field>
  )
)

interface NewProps extends Props {
  type?: 'text' | 'url' | 'number' | 'tel' | 'date' | 'password'
  autoCorrect?: boolean
  autoCapitalize?: boolean
  autoComplete?: boolean
  maxLength?: number
  showClearInput?: boolean
  showErrorMessage?: boolean
}

export const TextFieldNew: FC<NewProps> = flowMax(
  addDisplayName('TextFieldNew'),
  addDefaultProps(({ type }) => ({
    showClearInput: true,
    showVisibilityToggle: type === 'password',
    value: '',
    placeholder: '',
  })),
  addState('type', 'setType', ({ type }) => type ?? 'text'),
  addHandlers({
    onChange:
      ({ onChange }) =>
      ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        onChange(value)
      },
    onClear: ({ onClear, onChange }) =>
      onClear ??
      (() => {
        onChange('')
      }),
    onToggleVisibility:
      ({ type, setType }) =>
      () => {
        setType(type === 'password' ? 'text' : 'password')
      },
  }),
  addRef('ref', typedAs<HTMLInputElement | null>(null)),
  addLayoutEffectOnMount(({ focusOnMount, ref }) => () => {
    if (focusOnMount) {
      ref.current?.focus()
    }
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    id,
    type,
    label,
    value,
    onChange,
    onFocus,
    onKeyPress,
    onClear,
    showClearInput,
    showVisibilityToggle,
    onToggleVisibility,
    inputProps,
    error,
    showErrorMessage,
    disabled,
    noMargin,
    ref,
    suggestion,
    placeholder,
    autoCorrect,
    autoCapitalize,
    autoComplete,
    maxLength,
    className,
    inputClassName,
    classes,
  }) => (
    <FieldNew
      id={id}
      error={error}
      showErrorMessage={showErrorMessage}
      suggestion={suggestion}
      disabled={disabled}
      noMargin={noMargin}
      className={className}
    >
      <div className={classes.inputWrapper}>
        <input
          name={id}
          id={id}
          onChange={onChange}
          onFocus={onFocus}
          onKeyUp={onKeyPress}
          type={type}
          className={cx(
            classes.input,
            'focus:ring-0',
            !label && classes.inputNoLabel,
            error && classes.inputError,
            inputClassName
          )}
          value={value}
          disabled={disabled}
          ref={ref}
          placeholder={placeholder}
          spellCheck="false"
          autoCorrect={autoCorrect === false ? 'off' : undefined}
          autoCapitalize={autoCapitalize === false ? 'off' : undefined}
          autoComplete={autoComplete === false ? 'off' : undefined}
          maxLength={maxLength}
          {...inputProps}
        />
        {!!label && (
          <label
            htmlFor={id}
            className={cx(
              classes.label,
              !!value && classes.labelShrunk,
              error && classes.labelError
            )}
          >
            {label}
          </label>
        )}
        {!!value && showClearInput && !disabled && (
          <div className={classes.clearButtonContainer}>
            <IconButton
              onClick={onClear}
              name="clear-input"
              addHitSlop
              className={error ? classes.clearButtonError : classes.clearButton}
            />
          </div>
        )}
        {showVisibilityToggle && (
          <div className={classes.clearButtonContainer}>
            <IconButton
              onClick={onToggleVisibility}
              name={type === 'password' ? 'show' : 'hide'}
              addHitSlop
              className={error ? classes.clearButtonError : classes.clearButton}
            />
          </div>
        )}
      </div>
    </FieldNew>
  )
)

export const NumberField: FC<Props> = flowMax(
  addDisplayName('NumberField'),
  addHandlers({
    onChange:
      ({ onChange }) =>
      (value: string) => {
        onChange(value.replaceAll(/[^\d.]/g, ''))
      },
    onClear:
      ({ onChange }) =>
      () =>
        onChange(''),
  }),
  addProps(({ value }) => ({
    value: value?.toString() ?? '',
  })),
  (props) => <TextField {...props} />
)

export const NumberFieldNew: FC<Props> = flowMax(
  addDisplayName('NumberFieldNew'),
  addHandlers({
    onChange:
      ({ onChange }) =>
      (value: string) => {
        onChange(value.replaceAll(/[^\d.]/g, ''))
      },
    onClear:
      ({ onChange }) =>
      () =>
        onChange(''),
  }),
  addProps(({ value }) => ({
    value: value?.toString() ?? '',
  })),
  (props) => <TextFieldNew {...props} />
)

export const TextArea: FC<Props> = flowMax(
  addDisplayName('TextArea'),
  addHandlers({
    onChange:
      ({ onChange }) =>
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(event.target.value)
      },
  }),
  ({
    id,
    label,
    value,
    onChange,
    error,
    suggestion,
    noMargin,
    className,
    inputClassName,
  }) => (
    <Field
      id={id}
      error={error}
      suggestion={suggestion}
      noMargin={noMargin}
      className={className}
    >
      {label && (
        <label
          htmlFor={id}
          className={cx(styles.label, error && styles.labelError)}
        >
          {label}
        </label>
      )}
      <textarea
        name={id}
        id={id}
        onChange={onChange}
        value={value ?? ''}
        className={cx(styles.input, styles.area, inputClassName)}
      />
    </Field>
  )
)

export const TextAreaNew: FC<Props> = flowMax(
  addDisplayName('TextAreaNew'),
  addDefaultProps({
    value: '',
  }),
  addHandlers({
    onChange:
      ({ onChange }) =>
      ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
        onChange(value)
      },
    onClear:
      ({ onChange }) =>
      () =>
        onChange(''),
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    id,
    label,
    value,
    onChange,
    error,
    suggestion,
    placeholder,
    onClear,
    noMargin,
    disabled,
    className,
    inputClassName,
    wrapperClassName,
    classes,
  }) => (
    <FieldNew
      id={id}
      error={error}
      suggestion={suggestion}
      noMargin={noMargin}
      className={className}
      disabled={disabled}
    >
      <div className={cx(classes.inputWrapper, wrapperClassName)}>
        <textarea
          name={id}
          id={id}
          onChange={onChange}
          value={value}
          rows={5}
          placeholder={placeholder}
          className={cx(
            classes.input,
            classes.textarea,
            'focus:ring-0',
            !label && classes.inputNoLabel,
            inputClassName,
            error && classes.inputError
          )}
          disabled={disabled}
        />
        {!!label && (
          <label
            htmlFor={id}
            className={cx(
              classes.label,
              !!value && classes.labelShrunk,
              error && classes.labelError
            )}
          >
            {label}
          </label>
        )}
        {!!value && !disabled && (
          <div className={classes.clearButtonContainer}>
            <IconButton
              onClick={onClear}
              name="clear-input"
              addHitSlop
              className={error ? classes.clearButtonError : classes.clearButton}
            />
          </div>
        )}
      </div>
    </FieldNew>
  )
)
